// Code from here --- many thanks to authors. https://discourse.threejs.org/t/how-can-i-update-the-target-in-orbit-controls-based-on-camera-position-to-prevent-the-camera-from-tilting/31422

import { MutableRefObject, useEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { CameraControls } from "@react-three/drei";
import * as THREE from "three";

// Reference to a set of active KeyboardEvent.code entries
const useCodes = () => {
  const codes = useRef(new Set());
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => codes.current.add(e.code);
    const onKeyUp = (e: KeyboardEvent) => codes.current.delete(e.code);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);
  return codes;
};

interface WasdControlsProps {
  cameraControls: MutableRefObject<CameraControls | undefined>;
  movementSpeed: number;
  panSpeed: number;
  autoForward?: boolean;
  autoDown?: boolean;
}

export function WasdControls({
  cameraControls,
  movementSpeed,
  panSpeed,
  autoForward = false,
  autoDown = false,
}: WasdControlsProps) {
  const code = useCodes();

  useFrame((state, delta) => {
    const moveSpeed = code.current.has("ShiftLeft")
      ? movementSpeed * 3
      : movementSpeed;
    if (code.current.has("KeyW"))
      cameraControls.current?.forward(delta * moveSpeed, false);
    if (code.current.has("KeyA"))
      cameraControls.current?.truck(-delta * moveSpeed, 0, false);
    if (code.current.has("KeyS"))
      cameraControls.current?.forward(-delta * moveSpeed, false);
    if (code.current.has("KeyD"))
      cameraControls.current?.truck(delta * moveSpeed, 0, false);
    if (code.current.has("KeyQ"))
      cameraControls.current?.elevate(delta * moveSpeed, false);
    if (code.current.has("KeyE"))
      cameraControls.current?.elevate(-delta * moveSpeed, false);

    if (autoForward && cameraControls?.current?.camera) {
      cameraControls.current?.forward(movementSpeed * delta);
      const camera = cameraControls.current.camera;
      const cameraForward = new THREE.Vector3();
      camera.getWorldDirection(cameraForward);

      const cameraPosition = camera.position.clone();
      const targetPosition = new THREE.Vector3(-5, -1, 3); // Adjust if your model is actually elsewhere

      const angle = cameraForward.angleTo(
        new THREE.Vector3()
          .subVectors(targetPosition, cameraPosition)
          .normalize(),
      );

      // If angle is more than 90 degrees, rotate camera toward object
      if (angle > Math.PI / 2) {
        cameraControls.current.setLookAt(
          cameraPosition.x,
          cameraPosition.y,
          cameraPosition.z,
          targetPosition.x,
          targetPosition.y,
          targetPosition.z,
          true,
        );
      }
    }

    if (autoDown && cameraControls?.current?.camera) {
      cameraControls.current?.elevate(-movementSpeed * delta, false);
    }
  });

  return null;
}
